import React, {
  memo,
  useMemo,
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../../constants/styles';
import { FAILED } from '../../../actions/statusTypes';
import moment from 'moment';
import Cal, { expandRange, dateTimeIsPast } from '../../components/schedule_cal';

const AppointmentsCal = forwardRef((props, ref) => {
  const [selectedDate, setSelectedDate] = useState(props.selectedDate || null);
  const [selectedTimes, setSelectedTimes] = useState({});
  const [selectedRange, setSelectedRange] = useState(null);

  const resetSelectedTimes = useCallback(() => {
    setSelectedTimes({});
  }, []);
  useImperativeHandle(ref, () => ({
    resetSelectedTimes,
  }));

  const handleSelectedDate = useCallback(
    (date) => {
      setSelectedDate(date);
      props.onSelectedDate?.(date);
    },
    [props.onSelectedDate]
  );
  const handleSelectedRange = useCallback(
    (expandedRange, range) => {
      setSelectedRange(range);
      props.onSelectedRange?.(expandedRange);
    },
    [props.onSelectedRange]
  );
  useEffect(() => {
    if (props.selectedDate) {
      setSelectedDate(props.selectedDate);
    }
  }, [props.selectedDate]);
  useEffect(() => {
    setSelectedTimes({});
  }, [selectedDate, props.eventType]);

  useEffect(() => {
    props.onTimesChange?.(Object.keys(selectedTimes));
  }, [selectedTimes]);

  const handleTimeTap = (time) => {
    if (!props.multi) {
      setSelectedTimes({ [time]: true });
    } else {
      const newSelectedTimes = {
        ...selectedTimes,
        [time]: !selectedTimes[time],
      };
      if (!newSelectedTimes[time]) {
        delete newSelectedTimes[time];
      }
      setSelectedTimes(newSelectedTimes);
    }
  };

  // could do once and build for all days
  const times = useMemo(() => {
    if (props.hideTimes) return [];
    const availableTimes = (props.slotAvailability?.available || []).filter((dStr) => {
      return dStr.split('T')[0] === moment(selectedDate).format('YYYY-MM-DD');
    });
    const unavailableTimes = (props.slotAvailability?.unavailable || []).filter((dStr) => {
      return dStr.split('T')[0] === moment(selectedDate).format('YYYY-MM-DD');
    });
    return [...availableTimes, ...unavailableTimes]
      .map((dStr) => {
        const timeStr = dStr.split('T')[1];
        return timeStr ? timeStr.substring(0, 5) : null;
      })
      .filter(Boolean)
      .sort();
  }, [props.slotAvailability, selectedDate]);

  const timeTapHandlers = useMemo(() => {
    return times.reduce((acc, t) => {
      acc[t] = () => {
        handleTimeTap(t);
      };
      return acc;
    }, {});
  }, [times, selectedTimes]);

  function renderTime(t) {
    const selected = !!selectedTimes[t];

    const dateStr = `${moment(selectedDate).format('YYYY-MM-DD')}`;
    const dateTimeStr = `${dateStr}T${t}:00`;
    const isPast = props.disablePastSelect && dateTimeIsPast(dateTimeStr, props.timeZone);
    const available = !isPast && props.slotAvailability?.available?.includes(dateTimeStr);

    const availableStyle = !available ? styles.timeBubbleUnavailable : null;
    const selectedStyle = selected ? styles.timeBubbleSelected : null;
    const selectedTextStyle = selected ? styles.timeBubbleTextSelected : null;
    return (
      <Pressable
        key={t}
        onPress={timeTapHandlers[t]}
        style={[styles.timeBubble, selectedStyle, availableStyle]}
        disabled={!props.admin && !available}
      >
        <Text style={[styles.timeBubbleText, selectedTextStyle]}>{t}</Text>
      </Pressable>
    );
  }
  const range = selectedRange?.start && expandRange(selectedRange, !props.disablePastSelect);
  const selectedDateLabel = range?.length
    ? moment(range[0]).format('MMM DD') + ' - ' + moment(range[range.length - 1]).format('MMM DD')
    : moment(selectedDate).format('MMMM DD, YYYY');
  return (
    <View style={styles.container}>
      <View style={[styles.inputsContainer, { flex: 1 }]}>
        <View style={[styles.calContainer, props.calContainerStyle]}>
          <Cal
            className="rbc-schedule"
            calDisplayTextAsOffRangeForDate={props.calDisplayTextAsOffRangeForDate}
            calCellBGClassForDate={props.calCellBGClassForDate}
            selectedDate={selectedDate}
            onSelectedDate={handleSelectedDate}
            onSelectedRange={handleSelectedRange}
            firstAvailableDateFrom={props.firstAvailableDateFrom}
            multi={props.multi}
            events={props.events}
            dotStyle={styles.dot}
            onHoverIn={props.onHoverIn}
            onHoverOut={props.onHoverOut}
            disablePastSelect={props.disablePastSelect}
            canSelectSlot={props.canSelectSlot}
            arrowEnabled={props.arrowEnabled}
          />
        </View>

        <View style={[THEME.basicRow, { paddingTop: 20, flex: 1 }]}>
          <View style={{ flex: 1, height: 60, justifyContent: 'center' }}>
            <Text
              numberOfLines={1}
              style={[styles.selectedDateTitle, props.admin && __WEB__ ? { marginLeft: 27 } : null]}
            >
              {!!props.canSelectSlot(props.selectedDate) && selectedDateLabel}
            </Text>
          </View>
        </View>
      </View>

      {props.fetchStatus === FAILED && (
        <View style={[THEME.inputsContainer, { marginBottom: 0, paddingBottom: 0 }]}>
          <Text style={[THEME.errorMessageSmall, FONT.medium, { marginBottom: 0 }]}>
            There was an error fetching data. Please try again later.
          </Text>
        </View>
      )}
      {!selectedRange && !!times?.length && !!props.canSelectSlot(props.selectedDate) && (
        <View style={styles.timesContainer}>{times.map(renderTime)}</View>
      )}
    </View>
  );
});
const styles = StyleSheet.create({
  container: {
    // backgroundColor: 'yellow'
  },
  calContainer: {
    width: '100%',
  },
  selectedDateTitle: {
    ...FONT.medium,
    color: COLOR.text,
    fontSize: FONT_SIZE.huge,
    marginLeft: __WEB__ ? 0 : 10,
  },
  timesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginVertical: 20,
    paddingHorizontal: 20,
  },
  timeBubble: {
    ...THEME.textBubble,
    width: 75,
  },
  timeBubbleSelected: {
    ...THEME.textBubbleSelected,
  },
  timeBubbleUnavailable: {
    ...THEME.textBubbleDisabled,
  },
  timeBubbleText: {
    ...THEME.textBubbleText,
  },
  timeBubbleTextSelected: {
    ...THEME.textBubbleTextSelected,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: COLOR.emphasis,
    top: 1,
    right: 5,
  },
});

export default memo(AppointmentsCal);
